import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GridFilterModel, GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid-premium";
import { DialogMode, RunRequestErrorType, RunRequestManagementState } from "./runRequestManagementTypes";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useEffect } from "react";
import { requestApi, useGetRequestGetRequestThatRequierAttentionQuery } from "../../app/apislices/requestApi";
import { skipToken } from "@reduxjs/toolkit/query";
import dayjs from "dayjs";

const initialState: RunRequestManagementState = {
  selectedStart: dayjs().add(-1, "month").toISOString(),
  selectedEnd: new Date().toISOString(),
  selectedErrorTypes: ["failed", "admin", "user"],
  sortModel: [{ field: "to", sort: "desc" }],
  filterModel: { items: [] },
  rowSelectionModel: [],
  dialogMode: "none",
  dialogSortModel: [{ field: "from", sort: "desc" }],
  dialogFilterModel: { items: [] },
  dialogRowSelectionModel: [],
};

const runRequestManagementSlice = createSlice({
  name: "runRequestManagementSlice",
  initialState,
  reducers: {
    setSelectedStart: (state, action: PayloadAction<Date>) => {
      state.selectedStart = action.payload.toISOString();
    },
    setSelectedEnd: (state, action: PayloadAction<Date>) => {
      state.selectedEnd = action.payload.toISOString();
    },
    setSelectedErrorTypes: (state, action: PayloadAction<RunRequestErrorType[]>) => {
      state.selectedErrorTypes = action.payload;
    },
    setSortModel: (state, action: PayloadAction<GridSortModel>) => {
      state.sortModel = action.payload;
    },
    setFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
      state.filterModel = action.payload;
    },
    addFilter: (state, action: PayloadAction<[field: string, value: string]>) => {
      state.filterModel.items.push({
        field: action.payload[0],
        operator: "contains",
        value: action.payload[1],
      });
    },
    setRowSelectionModel: (state, action: PayloadAction<GridRowSelectionModel>) => {
      state.rowSelectionModel = action.payload;
    },
    setDialogMode: (state, action: PayloadAction<DialogMode>) => {
      state.dialogMode = action.payload;
      state.dialogRowSelectionModel = state.rowSelectionModel;
    },
    setDialogSortModel: (state, action: PayloadAction<GridSortModel>) => {
      state.dialogSortModel = action.payload;
    },
    setDialogFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
      state.dialogFilterModel = action.payload;
    },
    setDialogRowSelectionModel: (state, action: PayloadAction<GridRowSelectionModel>) => {
      state.dialogRowSelectionModel = action.payload;
    },
  },
});

export const {
  setSelectedStart,
  setSelectedEnd,
  setSelectedErrorTypes,
  setSortModel,
  addFilter,
  setFilterModel,
  setRowSelectionModel,
  setDialogMode,
  setDialogSortModel,
  setDialogFilterModel,
  setDialogRowSelectionModel,
} = runRequestManagementSlice.actions;

export default runRequestManagementSlice.reducer;

export const useGetCorrespondingAnalysisRequests = () => {
  const dispatch = useAppDispatch();

  const start = useAppSelector((state) => state.runRequestManagement.selectedStart);
  const end = useAppSelector((state) => state.runRequestManagement.selectedEnd);
  const selectedErrorTypes = useAppSelector((state) => state.runRequestManagement.selectedErrorTypes);
  const rowSelectionModel = useAppSelector((state) => state.runRequestManagement.rowSelectionModel);
  const dialogMode = useAppSelector((state) => state.runRequestManagement.dialogMode);

  const { data: requests } = useGetRequestGetRequestThatRequierAttentionQuery(
    dialogMode !== "none"
      ? {
          from: start,
          to: end,
          failed: selectedErrorTypes.includes("failed"),
          user: selectedErrorTypes.includes("user"),
          admin: selectedErrorTypes.includes("admin"),
          archived: selectedErrorTypes.includes("archived"),
        }
      : skipToken
  );

  useEffect(() => {
    if (!requests) {
      return;
    }

    const subscriptions = requests
      ?.filter((runRequest) => rowSelectionModel.find((x) => x === runRequest.id))
      .map((runRequest) => {
        return dispatch(
          requestApi.endpoints.getRequestGetRequestThatRequierAttention.initiate({
            from: runRequest.from,
            to: runRequest.to,
            failed: selectedErrorTypes.includes("failed") && dialogMode !== "dearchive",
            user: selectedErrorTypes.includes("user") && dialogMode !== "dearchive",
            admin: selectedErrorTypes.includes("admin") && dialogMode !== "dearchive",
            archived: dialogMode !== "archive",
          })
        );
      });
    return () => {
      for (const subscription of subscriptions) {
        subscription.unsubscribe();
      }
    };
  }, [dialogMode]);

  return useAppSelector((state) => {
    return requests && rowSelectionModel
      ? requests
          .filter((runRequest) => rowSelectionModel.find((x) => x == runRequest.id))
          .map((runRequest) => {
            return requestApi.endpoints.getRequestGetRequestThatRequierAttention.select({
              from: runRequest.from,
              to: runRequest.to,
              failed: selectedErrorTypes.includes("failed") && dialogMode !== "dearchive",
              user: selectedErrorTypes.includes("user") && dialogMode !== "dearchive",
              admin: selectedErrorTypes.includes("admin") && dialogMode !== "dearchive",
              archived: dialogMode !== "archive",
            })(state);
          })
      : undefined;
  });
};
